/**
 * Alternates: who is standing by for a dance, and why.
 *
 * Bobbie's model, in her words: "some classes have alternates from the start
 * and they need to be listed as that ... so if someone is sick the alternate
 * steps in!", "If it is assigned it should say Alternate in the dance
 * regardless", and "if a student misses 3 times in a ROW they are auto moved
 * to alternate. KIM can then approve them to move back to a permanent dancer".
 *
 * Kim's refinement (via Bobbie, 2026-07-31): "for comp it's not 3 in a row
 * missed but three period for a single dance - it flips to alternate until she
 * approves." So the count has two modes: a COMP dance flips on 3 missed TOTAL
 * since the reinstatement baseline, scattered or not; a recreational class
 * keeps the original 3-in-a-ROW rule exactly as it was.
 *
 * Two reasons a dancer is an alternate, and they are not the same thing:
 *   'assigned'   - a casting decision. Nothing is wrong. No warning color, no
 *                  absence count, and it is the one the theatre needs printed.
 *   'attendance' - the dance is not being rehearsed. A studio-internal status
 *                  that must never be printed as though it were casting.
 *
 * An alternate STAYS on the offering's studentIds. Alternate is an attribute of
 * a roster spot, not a parallel roster: attendance, costumes, comp readiness,
 * family visibility and the setEnrollment transaction all keep working and
 * merely gain the ability to render the spot differently. A second array would
 * drift out of sync and silently under-count the dance.
 *
 * This file is the arithmetic only. Its ONE import is ./attendanceIntegrity.ts,
 * which is itself dependency-free, so `node --experimental-strip-types` can
 * still run this directly in tests/alternates.test.mjs. The .ts extension on
 * that path is required, not a slip: node's loader will not resolve an
 * extensionless relative import, and hub/tsconfig.json sets
 * allowImportingTsExtensions so tsc accepts the same spelling. Both halves were
 * proven by running them. Every boundary takes an explicit instant rather than
 * reading the clock, so the tests pin real dates instead of "today".
 */

import { missRun, observeMeetings, totalMisses, type AttendanceMarkStatus } from './attendanceIntegrity.ts';

/** The four ways a class meeting can be marked. Mirrors AttendanceStatus. */
// 'excused' left this type on 2026-08-23 (Bobbie: "Excuses should be removed
// entirely"). Raw documents that still carry it - stale device caches from
// before scripts/remove-excused-marks.mjs ran - are folded into 'absent' at
// the parse boundary below, which preserves this module's math exactly: an
// excused mark always counted as a miss here.
export type AlternateAttendanceStatus = 'present' | 'absent' | 'late';

/** One attendance record, reduced to what the streak needs. */
export interface AlternateMark {
  /** 'YYYY-MM-DD'. Sorted and compared as a string, so the format matters. */
  date: string;
  status: AlternateAttendanceStatus;
}

/** Why a dancer is standing by. */
export type AlternateReason = 'assigned' | 'attendance';

/**
 * How misses are counted at one dance.
 *
 *   'consecutive' - the original rule, and still the recreational one: three
 *                   in a ROW, a present or late mark resets the run to zero.
 *   'total'       - Kim's comp rule: three missed PERIOD for that single
 *                   dance since the reinstatement baseline. Showing up in
 *                   between does not erase an earlier miss.
 */
export type AlternateCountMode = 'consecutive' | 'total';

/** Two is a quiet heads up. Three is the move. Same numbers in both modes. */
export const ALTERNATE_WARN_STREAK = 2;
export const ALTERNATE_MOVE_STREAK = 3;

/**
 * How far back a streak may reach. A dancer who missed the last three weeks of
 * LAST season and has not been back since is not currently failing to rehearse
 * this dance, and re-flagging them in September would be nonsense.
 */
export const ALTERNATE_LOOKBACK_DAYS = 120;

export type AlternateStreakLevel = 'clear' | 'warning' | 'move';

export interface AlternateStreak {
  /**
   * The miss count. In 'consecutive' mode, misses in a row counting back from
   * the most recent recorded mark; in 'total' mode, every miss inside the
   * window since the reinstatement baseline.
   */
  streak: number;
  /** The exact dates behind that count, oldest first. This is Kim's evidence. */
  dates: string[];
  level: AlternateStreakLevel;
  /** How the count was made, so wording can say "in a row" vs "total". */
  mode: AlternateCountMode;
}

export interface AlternateStreakOptions {
  /** The instant to measure from. Required so no boundary reads the clock. */
  nowMs: number;
  /**
   * Reinstatement baseline: a date on or before which nothing counts.
   *
   * Load bearing. Without it, the same three absences that got a dancer moved
   * are still the newest three marks the day after Kim approves them back, so
   * the flag returns immediately and Kim fights it again every Monday. Exclusive
   * on purpose - everything up to and including the day she said "you are back
   * in" is forgiven, including that evening's class.
   */
  sinceDate?: string | null;
  /** Defaults to ALTERNATE_LOOKBACK_DAYS. */
  windowDays?: number;
  /**
   * 'total' for comp dances, 'consecutive' (the default) for recreational
   * classes. Callers with an offering in hand should derive this via
   * alternateCountModeFor() in lib/offerings.ts rather than deciding here.
   */
  mode?: AlternateCountMode;
  /**
   * Every date this dance actually met inside the window: the class's own
   * cadence with closures taken out, from expandMeetingDates in
   * lib/attendanceIntegrity.ts. Only 'consecutive' mode can be changed by it,
   * and only together with registerDates below.
   *
   * Omit both and the answer is computed from the marks alone, exactly as it
   * always was. That is the honest default for a caller that does not know the
   * class's schedule: without it we cannot tell a meeting nobody wrote down
   * from a week the class never met, and guessing either way is worse than
   * counting only what is on the page.
   */
  meetingDates?: readonly string[];
  /**
   * Every date this dance registered ANY dancer. A meeting on this list with
   * no row for THIS dancer is a hole in the record, never an absence: see
   * missRun in lib/attendanceIntegrity.ts for what a hole does to a run.
   */
  registerDates?: readonly string[];
}

const DAY_MS = 24 * 60 * 60 * 1000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 'YYYY-MM-DD' for the instant `days` before nowMs, in UTC.
 *
 * Attendance dates are written in the studio's local time, so a UTC cutoff can
 * sit up to a day off the local one at the very edge of the window. Over a
 * 120 day lookback that is irrelevant, and pinning it to UTC is what lets a
 * test assert an exact boundary instead of whatever timezone CI happens to run
 * in.
 */
export function isoDaysBefore(nowMs: number, days: number): string {
  const d = new Date(nowMs - days * DAY_MS);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normalizeStatus(value: unknown): AlternateAttendanceStatus | null {
  if (value === 'present' || value === 'absent' || value === 'late') return value;
  // A pre-migration document from a stale cache. Absent is what it always
  // meant to this module's counting.
  if (value === 'excused') return 'absent';
  return null;
}

/**
 * Does this mark break the dancer's attendance at this dance?
 *
 * Dancer attendance has no excused status any more (Bobbie, 2026-08-23), and
 * when it did, excused counted as a miss here for a reason that still governs
 * the parse-boundary fold above: this measures whether THE DANCE IS BEING
 * REHEARSED, and an excused dancer was just as absent from the choreography
 * as an unexcused one. Jr staff attendance (lib/staffAttendanceMath.ts) KEEPS
 * its own excused status, where it means the opposite - a Jr assistant who
 * says on Monday they cannot make Thursday is being reliable, not absent -
 * and neither module should be changed to match the other.
 *
 * Late counts as there. They walked in.
 *
 * The parameter stays the RAW vocabulary (attendanceIntegrity's four-value
 * type, which the Jr staff lane legitimately shares) and 'excused' stays a
 * miss: this predicate is also called by observeMeetings on data that has not
 * been through a normalizer, and a raw pre-migration mark must count the way
 * it always counted rather than silently becoming "not a miss".
 */
export function isAlternateMiss(status: AttendanceMarkStatus): boolean {
  return status === 'absent' || status === 'excused';
}

/**
 * The miss count at one dance.
 *
 * In 'consecutive' mode (the default, and the recreational rule): misses in a
 * row counting back from the most recent mark - a present or late mark resets
 * the run. In 'total' mode (Kim's comp rule): every miss inside the window
 * since the reinstatement baseline, scattered or not - showing up in between
 * never erases an earlier miss. Either way a correction recomputes the answer
 * from the marks, so fixing a wrong absence drops the count the same day.
 *
 * PER DANCE ONLY. Never merge marks from two classes into this: a merged
 * timeline invents runs of absences that never happened at any single dance,
 * and would move a dancer out of a dance they have not missed once.
 *
 * A MISSING ROW IS NEVER A MISS, in either mode. What it IS depends on what
 * the caller can tell us about the calendar (see meetingDates above):
 *
 *   - No calendar supplied: an untaken date is invisible. It neither counts
 *     nor breaks, because this function is only looking at rows somebody
 *     actually wrote. A teacher who forgets to open attendance must not be
 *     able to demote anybody, and must not be able to un-demote anybody
 *     either.
 *   - Calendar supplied: a date the class met but nobody registered behaves
 *     the same way. A date the class met and REGISTERED, where this dancer
 *     has no row, is a hole: still not a miss, but a later miss starts the
 *     count again, because "three in a row" cannot span a meeting nobody
 *     answered for. A closed day and an off-week for an A/B class are not
 *     meetings at all, so neither one can break a real run.
 */
export function computeAlternateStreak(
  marks: readonly AlternateMark[],
  opts: AlternateStreakOptions
): AlternateStreak {
  const cutoff = isoDaysBefore(opts.nowMs, opts.windowDays ?? ALTERNATE_LOOKBACK_DAYS);
  const since = typeof opts.sinceDate === 'string' && ISO_DATE.test(opts.sinceDate)
    ? opts.sinceDate
    : null;
  const mode: AlternateCountMode = opts.mode === 'total' ? 'total' : 'consecutive';

  // One record per date is guaranteed by the attendance doc id, but a
  // half-written demo row or a duplicate would otherwise count twice, so the
  // newest entry for a date wins and the rest are dropped.
  const byDate = new Map<string, AlternateAttendanceStatus>();
  for (const mark of marks) {
    if (!mark || !ISO_DATE.test(mark.date)) continue;
    const status = normalizeStatus(mark.status);
    if (!status) continue;
    if (mark.date < cutoff) continue;
    if (since !== null && mark.date <= since) continue;
    byDate.set(mark.date, status);
  }

  // The calendar gets the same window and baseline treatment the marks do, so
  // a meeting from before the reinstatement can never reach forward and break
  // a run Kim already forgave.
  const inWindow = (date: string): boolean =>
    date >= cutoff && (since === null || date > since);

  const observations = observeMeetings({
    marks: [...byDate].map(([date, status]) => ({ date, status })),
    meetingDates: (opts.meetingDates ?? []).filter(inWindow),
    registerDates: (opts.registerDates ?? []).filter(inWindow),
    isMiss: isAlternateMiss,
  });

  // Only the recreational rule forgives on attendance. On a comp dance every
  // miss stays counted until Kim's reinstatement resets the baseline, so
  // 'total' just adds them up and no unknown can ever change that number.
  const dates =
    mode === 'total' ? totalMisses(observations) : missRun(observations).currentDates;
  const streak = dates.length;

  return {
    streak,
    dates,
    level:
      streak >= ALTERNATE_MOVE_STREAK
        ? 'move'
        : streak >= ALTERNATE_WARN_STREAK
          ? 'warning'
          : 'clear',
    mode,
  };
}

/** Plain words for a streak, for a roster subtitle. Empty when there is none. */
export function describeAlternateStreak(streak: AlternateStreak): string {
  if (streak.streak === 0) return '';
  if (streak.mode === 'total') {
    if (streak.level === 'move') {
      return `Missed ${streak.streak} total: ${streak.dates.join(', ')}`;
    }
    return `Missed ${streak.streak} total.`;
  }
  if (streak.level === 'move') {
    return `Missed the last ${streak.streak} in a row: ${streak.dates.join(', ')}`;
  }
  return `Missed the last ${streak.streak} in a row.`;
}

/**
 * What a dancer's spot says wherever the dance is listed.
 *
 * Always the same word, whatever the reason. Bobbie: "If it is assigned it
 * should say Alternate in the dance regardless." The reason changes the color
 * and the subtitle, never the label.
 */
export const ALTERNATE_LABEL = 'Alternate';

/**
 * True when this alternate should be shown WITHOUT warning color and without
 * an absence count. An assigned alternate is doing the job they were given,
 * not in trouble, and dressing that as a problem is wrong to their face.
 */
export function isPlannedAlternate(reason: AlternateReason | null | undefined): boolean {
  return reason === 'assigned';
}

/** Reason wording for staff surfaces, where the distinction is the point. */
export function alternateReasonLabel(reason: AlternateReason | null | undefined): string {
  if (reason === 'assigned') return 'Alternate by assignment';
  if (reason === 'attendance') return 'Alternate after missed rehearsals';
  return '';
}

/**
 * What the PRINTED PROGRAM says next to a dancer's name.
 *
 * Only an ASSIGNED alternate is marked. That is the casting fact the theatre
 * asked for, and it is safe to print because nothing is wrong. An
 * attendance-driven alternate gets nothing: printing them as "Alternate" in a
 * public program would publish a discipline outcome dressed up as a planned
 * casting choice, which is exactly the conflation this whole distinction
 * exists to prevent. Staff still see it everywhere inside the Hub, and the
 * internal roster export below carries the full reason.
 */
export function programAlternateSuffix(reason: AlternateReason | null | undefined): string {
  return isPlannedAlternate(reason) ? ` (${ALTERNATE_LABEL})` : '';
}

/**
 * The Status cell in the internal roster export (sign-in sheets, competition
 * registration). This one DOES separate the two, because a roster Kim prints
 * and reads is the sheet where "by design" versus "because they have not been
 * here" changes what she does about it.
 */
export function rosterAlternateStatus(reason: AlternateReason | null | undefined): string {
  if (reason === 'assigned') return 'Alternate (assigned)';
  if (reason === 'attendance') return 'Alternate (missed rehearsals)';
  return '';
}

/**
 * The note left on the absent dancer's attendance record when an alternate
 * steps in. A note on that date, not a roster rewrite: the alternate did not
 * become a full dancer by covering once, and the absent dancer did not lose
 * their spot by being sick on a Tuesday.
 */
export function stepInNote(alternateName: string): string {
  const name = alternateName.trim();
  return name ? `Alternate covered: ${name}` : '';
}

/**
 * Strip any previous step-in note before writing a new one, so covering twice
 * does not stack "Alternate covered: A Alternate covered: B" on one record.
 */
export function withStepInNote(existingNotes: string, alternateName: string): string {
  const kept = existingNotes
    .split('\n')
    .filter((line) => !line.trim().startsWith('Alternate covered:'))
    .join('\n')
    .trim();
  const note = stepInNote(alternateName);
  if (!note) return kept;
  return kept ? `${kept}\n${note}` : note;
}

// ---------------------------------------------------------------------------
// Attendance concern trigger: a separate feature, same arithmetic.
//
// Kim's ask, via Bobbie (2026-08): "if they are comp if they have missed 3
// and for rec kids anytime they miss 2 in a row of a specific class, some
// sort of trigger warning for me to check on them." This is NOT the
// alternates auto-move above - nobody's roster spot changes here, this is
// only a flag telling Kim who to call. It reuses computeAlternateStreak
// rather than a second definition of "missed too much" that could quietly
// disagree with it, and reuses the SAME numbers this file already names:
// ALTERNATE_WARN_STREAK (2, "a quiet heads up" for the alternates feature)
// is Kim's rec bar, and ALTERNATE_MOVE_STREAK (3, "the move" over there) is
// her comp bar, both counted total for one comp dance.
// ---------------------------------------------------------------------------

/** Which of Kim's two rules fired. */
export type AttendanceConcernRule = 'comp-total-3' | 'rec-consecutive-2';

export interface AttendanceConcernFlag {
  rule: AttendanceConcernRule;
  /** Plain words for the rule that fired, so a comp "3 total" and a rec "2 in a row" never read as the same finding. */
  reason: string;
  /** The exact miss count behind the flag (>=3 for comp-total-3, >=2 for rec-consecutive-2). */
  missCount: number;
  /** The exact dates behind the count, oldest first - Kim's evidence. */
  missDates: string[];
}

export interface AttendanceConcernInput {
  /** 'total' for a comp dance, 'consecutive' for a rec class. Derive with alternateCountModeFor() in lib/offerings.ts. */
  mode: AlternateCountMode;
  marks: readonly AlternateMark[];
  nowMs: number;
  /** Reinstatement baseline, if this pair was ever moved to alternate and approved back. See alternateStreakBaseline() in lib/offerings.ts. */
  sinceDate?: string | null;
  /** The class's real meeting sequence and its registers. Same meaning and same optionality as on AlternateStreakOptions above. */
  meetingDates?: readonly string[];
  registerDates?: readonly string[];
}

/**
 * The concern for one (offering, student) pair, or null when neither of
 * Kim's two bars is cleared yet. A comp dance only ever returns
 * 'comp-total-3' (or null); a rec class only ever returns 'rec-consecutive-2'
 * (or null) - the mode picks which single rule applies, there is no mixing.
 */
export function attendanceConcernFor(input: AttendanceConcernInput): AttendanceConcernFlag | null {
  const streak = computeAlternateStreak(input.marks, {
    nowMs: input.nowMs,
    sinceDate: input.sinceDate,
    mode: input.mode,
    meetingDates: input.meetingDates,
    registerDates: input.registerDates,
  });
  const fired =
    input.mode === 'total'
      ? streak.streak >= ALTERNATE_MOVE_STREAK
      : streak.streak >= ALTERNATE_WARN_STREAK;
  if (!fired) return null;
  return {
    rule: input.mode === 'total' ? 'comp-total-3' : 'rec-consecutive-2',
    reason:
      input.mode === 'total'
        ? `Missed ${streak.streak} rehearsal${streak.streak === 1 ? '' : 's'} total for this comp dance.`
        : `Missed the last ${streak.streak} in a row for this class.`,
    missCount: streak.streak,
    missDates: streak.dates,
  };
}
